'use client';
import { getActiveAccountId } from '#pplx/frontend/browser-storage/activeAccount.js';
import { SafeLocalStorage } from '#pplx/frontend/browser-storage/localStorage.js';
import { readCachedFlagValue } from '#pplx/frontend/experimentation/cachedFlagValue.js';
import { FeatureFlagsContext } from '#pplx/frontend/experimentation/context.js';
import { useCallback, useContext, useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
function getLastVariationResult(lastVariation, defaultValue, evaluationContext, flag, skip) {
    if (!lastVariation ||
        lastVariation.flag !== flag ||
        lastVariation.evaluationContext !== evaluationContext ||
        lastVariation.skip !== skip ||
        lastVariation.defaultValue !== defaultValue) {
        return null;
    }
    return { variation: lastVariation.variation, loading: false };
}
const NO_SUBSCRIPTION = () => () => undefined;
const LATEST_FLAG_RESULT = Symbol('latestFlagResult');
/**
 * Reads a flag hook's current result without subscribing its component to later changes. Tracked
 * results re-check their inputs, so a read right after a published change sees it. Results from
 * hooks that do not track reads are returned unchanged.
 */
function readLatestFlagResult(result) {
    return result[LATEST_FLAG_RESULT]?.() ?? result;
}
function useFeatureFlagsSelection(flags, select) {
    const getSelection = () => select(flags);
    return useSyncExternalStore(flags.subscribe ?? NO_SUBSCRIPTION, getSelection, getSelection);
}
function resolveFlagResult(flags, { defaultValue, evaluationContext, flag, skip }, lastVariationRef) {
    // Skipped hooks resolve to defaultValue so ineligible users stay out of the
    // assignment denominator instead of being assigned into a fallback branch.
    if (skip) {
        return { variation: defaultValue, loading: false };
    }
    const flagParams = { flag, defaultValue, evaluationContext };
    const override = flags.getFlagOverride(flagParams);
    if (override.shouldUseOverride) {
        return { variation: override.value ?? defaultValue, loading: false };
    }
    if (!flags.isOpenFeatureFlagReady(flag)) {
        const lastVariation = getLastVariationResult(lastVariationRef.current, defaultValue, evaluationContext, flag, skip);
        if (lastVariation)
            return lastVariation;
        return { variation: defaultValue, loading: true };
    }
    const variation = flags.getFlagValue(flagParams);
    lastVariationRef.current = { defaultValue, evaluationContext, flag, skip, variation };
    return { variation, loading: false };
}
/**
 * Resolves one flag value using the same override/readiness rules as the hooks.
 * Timeout or cancellation returns defaultValue without evaluating an unready flag.
 * The caller owns cancellation; route changes do not implicitly cancel the wait.
 */
export function waitForFlagValue(flags, params, { timeoutMs, signal }) {
    return new Promise((resolve, reject) => {
        let unsubscribe;
        let settled = false;
        const lastVariationRef = { current: null };
        const timeout = setTimeout(() => finish(params.defaultValue), timeoutMs);
        const cleanup = () => {
            settled = true;
            clearTimeout(timeout);
            unsubscribe?.();
            signal?.removeEventListener('abort', cancel);
        };
        const finish = (variation) => {
            cleanup();
            resolve(variation);
        };
        const cancel = () => finish(params.defaultValue);
        const check = () => {
            if (settled)
                return;
            if (signal?.aborted)
                return cancel();
            try {
                const { variation, loading } = resolveFlagResult(flags, params, lastVariationRef);
                if (!loading)
                    finish(variation);
            }
            catch (error) {
                cleanup();
                reject(error);
            }
        };
        signal?.addEventListener('abort', cancel, { once: true });
        check();
        if (!settled) {
            try {
                unsubscribe = flags.subscribe?.(check);
                // Custom contexts may notify synchronously when subscribing.
                if (settled)
                    unsubscribe?.();
            }
            catch (error) {
                cleanup();
                reject(error);
            }
        }
    });
}
export function makeCachedFlagValueHook(cacheKey, useVariation, overrideFlag) {
    // Account activation can finish after the hook's first render, so cache each
    // storage namespace independently instead of pinning the pre-activation value.
    const cacheByAccount = new Map();
    return function useCachedFlagValue({ defaultValue, skip }) {
        // The cached value is pinned for the page, so the live assignment is only read when it is
        // persisted below; reading it here would re-render this host when flags resolve.
        const result = useVariation({ defaultValue, skip });
        const defaultValueRef = useRef(defaultValue);
        const activeAccountId = getActiveAccountId();
        const flags = useContext(FeatureFlagsContext);
        const { getFlagOverride } = flags;
        const hasProcessedUrlOverrides = useFeatureFlagsSelection(flags, (value) => value.hasProcessedUrlOverrides);
        const pageOverride = useRef(null);
        // opt in to the processed page override; later cross-tab cookie changes must not switch UI.
        if (overrideFlag &&
            !skip &&
            (!pageOverride.current ||
                pageOverride.current.accountId !== activeAccountId ||
                (!pageOverride.current.processed && hasProcessedUrlOverrides))) {
            pageOverride.current = {
                accountId: activeAccountId,
                processed: hasProcessedUrlOverrides,
                result: getFlagOverride({ flag: overrideFlag, defaultValue }),
            };
        }
        if (!cacheByAccount.has(activeAccountId)) {
            cacheByAccount.set(activeAccountId, readCachedFlagValue(cacheKey, defaultValueRef.current));
        }
        useEffect(() => {
            const persistLiveAssignment = () => {
                if (getActiveAccountId() !== activeAccountId) {
                    return;
                }
                const { variation, loading } = readLatestFlagResult(result);
                if (!skip &&
                    !loading &&
                    variation !== readCachedFlagValue(cacheKey, defaultValueRef.current)) {
                    // Nothing reads this key reactively, so write it without re-rendering the host.
                    SafeLocalStorage.setItem(cacheKey, JSON.stringify(variation));
                }
            };
            persistLiveAssignment();
            // The read above re-checks the flag's inputs, so it does not matter whether this runs before
            // or after the inner hook's own subscription.
            return flags.subscribe?.(persistLiveAssignment);
        }, [activeAccountId, flags, result, skip]);
        if (overrideFlag) {
            if (skip)
                return defaultValue;
            const override = pageOverride.current?.result;
            if (override?.shouldUseOverride)
                return override.value ?? defaultValue;
        }
        return cacheByAccount.get(activeAccountId);
    };
}
/**
 * Creates a hook that evaluates a feature flag.
 * @param params - The parameters for the hook.
 * @returns A hook that evaluates a feature flag.
 */
function useFlagValue(params) {
    const flags = useContext(FeatureFlagsContext);
    if (!flags) {
        throw new Error('Feature flag hooks must be used within FeatureFlagsContext');
    }
    const { defaultValue, evaluationContext, flag, skip } = params;
    const lastVariationRef = useRef(null);
    const resolvedRef = useRef(null);
    const publishedRef = useRef(null);
    // Like TanStack Query's tracked results, a change re-renders a component only if it read the
    // changed field while rendering. Flags becoming ready flips `loading` for every hook while few
    // variations change, and cached hooks read neither.
    const readsVariationRef = useRef(false);
    const readsLoadingRef = useRef(false);
    // React reads the snapshot on every render and again after commit, so evaluate only when an
    // input changed: `getFlagValue` need not return the same object twice, and a new object per read
    // would re-render forever.
    const resolveLatest = useCallback(() => {
        const inputs = [
            defaultValue,
            evaluationContext,
            flag,
            skip,
            flags.getFlagOverride,
            flags.getFlagValue,
            // URL override processing mutates the override store outside React.
            flags.hasProcessedUrlOverrides,
            flags.isOpenFeatureFlagReady,
            flags.getVersion?.(),
        ];
        const resolved = resolvedRef.current;
        if (resolved && inputs.every((input, index) => Object.is(input, resolved.inputs[index]))) {
            return resolved.result;
        }
        const result = resolveFlagResult(flags, { defaultValue, evaluationContext, flag, skip }, lastVariationRef);
        resolvedRef.current = { inputs, result };
        return result;
    }, [flags, defaultValue, evaluationContext, flag, skip]);
    const getSnapshot = useCallback(() => {
        const latest = resolveLatest();
        const published = publishedRef.current;
        if (published &&
            (Object.is(published.variation, latest.variation) || !readsVariationRef.current) &&
            (published.loading === latest.loading || !readsLoadingRef.current)) {
            return published;
        }
        publishedRef.current = latest;
        return latest;
    }, [resolveLatest]);
    const published = useSyncExternalStore(flags.subscribe ?? NO_SUBSCRIPTION, getSnapshot, getSnapshot);
    return useMemo(() => {
        // `published` is only the identity: reads resolve again, because code that runs on a published
        // change can run before React has re-read this hook.
        const result = {
            get variation() {
                readsVariationRef.current = true;
                return resolveLatest().variation;
            },
            get loading() {
                readsLoadingRef.current = true;
                return resolveLatest().loading;
            },
        };
        Object.defineProperty(result, LATEST_FLAG_RESULT, { value: resolveLatest });
        return result;
        // eslint-disable-next-line react-hooks/exhaustive-deps -- `published` changes the identity.
    }, [published, resolveLatest]);
}
export function useObjectFlagValue(params) {
    return useFlagValue(params);
}
export function makeObjectFlagValueHook(flag) {
    return function useBoundObjectFlagValue({ defaultValue, evaluationContext, skip, }) {
        return useObjectFlagValue({ flag, defaultValue, evaluationContext, skip });
    };
}
export function useStringFlagValue(params) {
    return useFlagValue(params);
}
export function makeStringFlagValueHook(flag) {
    return function useBoundStringFlagValue({ defaultValue, evaluationContext, skip, }) {
        return useStringFlagValue({ flag, defaultValue, evaluationContext, skip });
    };
}
export function useBooleanFlagValue(params) {
    return useFlagValue(params);
}
export function makeBooleanFlagValueHook(flag) {
    return function useBoundBooleanFlagValue({ defaultValue, evaluationContext, skip, }) {
        return useBooleanFlagValue({ flag, defaultValue, evaluationContext, skip });
    };
}
export function useBooleanNonExperimentFlagValue(params) {
    return useBooleanFlagValue({
        ...params,
        skip: false,
    });
}
/**
 * The flags context for code that reads it outside the flag hooks. It re-renders on every change
 * the provider publishes and returns fresh function identities, so memos and effects keyed on
 * them re-run the way they did when every change replaced the context value.
 */
export function useFeatureFlagsContext() {
    const flags = useContext(FeatureFlagsContext);
    if (!flags) {
        throw new Error('useFeatureFlags must be used within FeatureFlagsContext');
    }
    const version = useFeatureFlagsSelection(flags, (value) => value.getVersion?.());
    return useMemo(() => ({
        appliedEppoUserId: flags.appliedEppoUserId,
        getFlagOverride: (params) => flags.getFlagOverride(params),
        getFlagValue: (params) => flags.getFlagValue(params),
        getVersion: flags.getVersion,
        hasLoadedUserInfo: flags.hasLoadedUserInfo,
        hasProcessedUrlOverrides: flags.hasProcessedUrlOverrides,
        isOpenFeatureFlagReady: (flag) => flags.isOpenFeatureFlagReady(flag),
        subscribe: flags.subscribe,
    }), 
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `version` marks a published change.
    [flags, version]);
}
export const useFeatureFlags = () => useFeatureFlagsContext();
//# sourceMappingURL=hooks.js.map